import { AttentionProvider } from './context/AttentionContext';
import AttentionPage from './pages/AttentionPage';
import React, { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ProjectsPage from './pages/ProjectsPage';
import ProjectPage from './pages/ProjectPage';
import SessionPage from './pages/SessionPage';
import SkillPackagesPage from './pages/SkillPackagesPage';
import AgentsPage from './pages/AgentsPage';
import HumanRequestPage from './pages/HumanRequestPage';
import LoginPage from './pages/LoginPage';
import SetupPage from './pages/SetupPage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { api } from './api/client';
import './App.css';
import './styles/theme.css';
import './styles/design.css';
import { useCapabilities } from './hooks/useCapabilities';
import AppShell from './components/AppShell';
import { BrandSplash } from './components/BrandMark';
import { ThemeProvider } from './context/ThemeContext';
import { UIProvider } from './context/UIContext';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <BrandSplash />;
  if (!user) return <Navigate to="/login" state={{ returnTo: location.pathname + location.search }} replace />;
  return children;
}

// Checks whether the platform is initialized (any users exist).
// Shows a splash while checking, then routes accordingly.
function InitGuard({ children }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'setup' | 'ready'
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const publicPaths = ['/login', '/setup'];
    if (publicPaths.includes(location.pathname)) {
      setStatus('ready');
      return;
    }
    Promise.all([api.checkInitialized(), api.getCapabilities()])
      .then(([{ initialized }, capabilities]) => {
        if (!initialized) {
          setStatus('setup');
          navigate(capabilities.self_setup ? '/setup' : '/login', { replace: true });
        } else {
          setStatus('ready');
        }
      })
      .catch(() => setStatus('ready')); // on error, let auth handle it
  }, []); // eslint-disable-line

  if (status === 'loading') {
    return <BrandSplash />;
  }
  return children;
}

function SetupGate() {
  const capabilities = useCapabilities();
  return capabilities.self_setup ? <SetupPage /> : <div className="alert">Учётную запись выдаёт администратор. <Link to="/login">Войти</Link></div>;
}

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login' || location.pathname === '/setup';
  const isSession = /\/sessions\//.test(location.pathname) && !/human-requests/.test(location.pathname);

  if (isLoginPage) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/setup" element={<SetupGate />} />
      </Routes>
    );
  }

  return (
    <ProtectedRoute><AttentionProvider><AppShell session={isSession}>
      {isSession ? (
        <Routes>
          <Route path="/projects/:projectId/sessions/:sessionId" element={
            <ProtectedRoute><SessionPage /></ProtectedRoute>
          } />
        </Routes>
      ) : (
        <div className="page-content">
          <Routes>
            <Route path="/" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
            <Route path="/attention" element={<AttentionPage />} />
            <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectPage /></ProtectedRoute>} />
            <Route path="/sessions/:sessionId/human-requests" element={<ProtectedRoute><HumanRequestPage /></ProtectedRoute>} />
            <Route path="/skills" element={<Navigate to={user?.is_admin ? '/admin/skills' : '/my-skills'} replace />} />
            <Route path="/my-skills" element={<SkillPackagesPage />} />
            <Route path="/projects/:projectId/skills" element={<SkillPackagesPage />} />
            <Route path="/admin/skills" element={<SkillPackagesPage />} />
            <Route path="/agents" element={<ProtectedRoute><AgentsPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          </Routes>
        </div>
      )}
    </AppShell></AttentionProvider></ProtectedRoute>
  );
}

const router = createBrowserRouter([{ path: '*', element: <InitGuard><AppRoutes /></InitGuard> }]);

export default function App() {
  return (
    <UIProvider><AuthProvider><ThemeProvider>
      <RouterProvider router={router} />
    </ThemeProvider></AuthProvider></UIProvider>
  );
}
